// Manual classification write — `PUT /api/ontology/tags/classification`.

import type { TagClassificationRecord, TagLlmClass, TagLlmRole } from './types'
import { classificationFromJson, sendJson } from './wire'

/**
 * The user-editable subset of a classification. Confidence/model/timestamps
 * are stamped server-side (`model='manual'`, `confidence='high'`).
 */
export interface TagClassificationDraft {
  nodeId: string
  tagPath: string
  tagClass: TagLlmClass
  role?: TagLlmRole | null
  isPercentage?: boolean | null
  expectedRangeLow?: number | null
  expectedRangeHigh?: number | null
  goodStates?: string[] | null
  badStates?: string[] | null
  warningStates?: string[] | null
  reasoning?: string
}

/**
 * Manually set (or overwrite) one tag's classification via
 * `PUT /api/ontology/tags/classification`. The server validates strictly
 * (bad enums / inverted ranges are a 400 whose message is thrown here) and
 * persists with `model='manual'`. Returns the stored record so callers can
 * update their classification map without a refetch.
 */
export async function saveTagClassification(
  draft: TagClassificationDraft,
  signal?: AbortSignal
): Promise<TagClassificationRecord> {
  const body: Record<string, unknown> = {
    node_id: draft.nodeId,
    tag_path: draft.tagPath,
    tag_class: draft.tagClass
  }
  if (draft.role != null) body.role = draft.role
  if (draft.isPercentage != null) body.is_percentage = draft.isPercentage
  if (draft.expectedRangeLow != null) body.expected_range_low = draft.expectedRangeLow
  if (draft.expectedRangeHigh != null) body.expected_range_high = draft.expectedRangeHigh
  if (draft.goodStates?.length) body.good_states = draft.goodStates
  if (draft.badStates?.length) body.bad_states = draft.badStates
  if (draft.warningStates?.length) body.warning_states = draft.warningStates
  if (draft.reasoning) body.reasoning = draft.reasoning

  const resp = await sendJson<{ classification?: unknown }>(
    'PUT',
    '/tags/classification',
    body,
    signal
  )
  const stored = classificationFromJson(resp.classification ?? null)
  if (stored === null) {
    throw new Error('server did not return the stored classification')
  }
  return stored
}
